import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft, Eye, EyeOff, CheckCircle2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BrandPanel = () => (
  <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-border">
    <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(hsl(var(--primary)) 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
    <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
    <div className="relative flex items-center gap-2.5">
      <img src="/logo.png" alt="Arqitek" className="h-8 w-auto object-contain" />
    </div>
    <div className="relative max-w-md">
      <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4">Arqitek · Account recovery</div>
      <h1 className="font-display text-5xl font-bold tracking-tight leading-[1.05]">
        Locked out?<br /><span className="text-primary text-teal-glow">Back in a minute.</span>
      </h1>
      <p className="mt-5 text-muted-foreground text-base leading-relaxed">
        We'll email a 6-digit code to your account address. Enter it here with a
        new password to get straight back to your work queue.
      </p>
    </div>
    <div className="relative text-xs uppercase tracking-[0.2em] text-muted-foreground">
      Secure · Single-use code · Expires in 15 min
    </div>
  </div>
);

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("request"); // request | reset | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success(data.message || "If an account exists, a code was sent.");
      setStep("reset");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPassword,
      });
      setStep("done");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <BrandPanel />
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm" data-testid="forgot-password-page">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <img src="/logo.png" alt="Arqitek" className="h-8 w-auto object-contain" />
          </div>

          {step === "request" && (
            <>
              <h2 className="font-display text-3xl font-bold tracking-tight">Reset your password</h2>
              <p className="text-sm text-muted-foreground mt-1.5 mb-8">
                Enter your account email and we'll send you a 6-digit code.
              </p>
              <form onSubmit={requestCode} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="forgot-email" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
                <Button type="submit" className="w-full group" disabled={loading} data-testid="forgot-submit">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Send reset code
                  {!loading && <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />}
                </Button>
              </form>
            </>
          )}

          {step === "reset" && (
            <>
              <div className="mb-4 inline-flex items-center gap-2 text-primary">
                <MailCheck className="h-5 w-5" />
                <span className="text-xs uppercase tracking-[0.2em]">Code sent</span>
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight">Enter your code</h2>
              <p className="text-sm text-muted-foreground mt-1.5 mb-8">
                We sent a 6-digit code to <span className="text-foreground font-medium">{email}</span>. It expires in 15 minutes.
              </p>
              <form onSubmit={resetPassword} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="fp-code">6-digit code</Label>
                  <Input
                    id="fp-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="tracking-[0.5em] text-center text-lg"
                    required
                    autoFocus
                    data-testid="reset-code"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fp-new">New password</Label>
                  <div className="relative">
                    <Input id="fp-new" type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} className="pr-11" data-testid="reset-new-password" />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="reset-password-toggle"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fp-confirm">Confirm new password</Label>
                  <Input id="fp-confirm" type={showPassword ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="reset-confirm-password" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
                <Button type="submit" className="w-full group" disabled={loading} data-testid="reset-submit">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Update password
                  {!loading && <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />}
                </Button>
                <button type="button" onClick={requestCode} disabled={loading} className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="resend-code">
                  Didn't get it? Resend code
                </button>
              </form>
            </>
          )}

          {step === "done" && (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="font-display text-3xl font-bold tracking-tight">Password updated</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">
                Your password has been changed. Sign in with your new credentials.
              </p>
              <Button className="w-full group" onClick={() => navigate("/login")} data-testid="back-to-login-btn">
                Back to sign in
                <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>
          )}

          {step !== "done" && (
            <div className="mt-8">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-login-link">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
